import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Typography, Grid, TextField, Button, Box, Pagination, FormControl, InputLabel, Select, MenuItem, Chip, Autocomplete, IconButton, useMediaQuery, Theme as MuiTheme, ToggleButton, ToggleButtonGroup } from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import { searchGames, getGenres, getPlatforms, getBasicGameStatuses, getThemes } from '../services/api';
import { searchAnime, AnimeSearchFilters, getBasicAnimeStatuses } from '../services/animeApi';
import { Game, Genre, Platform, Theme as GameTheme } from '../types/Game';
import { Anime } from '../types/Anime';
import LoadingPlaceholder from '../components/LoadingPlaceholder';
import { SelectChangeEvent } from '@mui/material/Select';
import GameCard from '../components/GameCard';
import AnimeCard from '../components/AnimeCard';
import Advertisement from '../components/advertisement/Ad_wide_728';
import RectangleAdvertisement from '../components/advertisement/Ad_rectangle_300';
import { useTheme } from '@mui/material/styles';
import AdvertisementResponsive from '../components/advertisement/Ad_GA_res';
import { isPwa } from '../utils/isPwa';
import { isDev } from '../utils/env';

// SearchFilters インターフェースを更新
interface SearchFilters {
  genres?: number[];
  platforms?: number[];
  themes?: number[];
  year?: number | '';
  order?: string;
}

type SearchMode = 'game' | 'anime';

const ANIME_TYPES = ['TV', 'Movie', 'OVA', 'ONA', 'Special', 'Music'] as const;
const ANIME_SEASONS = [
  { value: 'winter', label: '冬 (1-3月)' },
  { value: 'spring', label: '春 (4-6月)' },
  { value: 'summer', label: '夏 (7-9月)' },
  { value: 'fall', label: '秋 (10-12月)' },
] as const;

const GAMES_PER_PAGE = 30;
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_RANGE = Array.from({ length: CURRENT_YEAR - 1970 + 1 }, (_, i) => CURRENT_YEAR - i);

const SearchPage: React.FC = () => {
  const [searchResults, setSearchResults] = useState<Game[]>([]);
  const [animeResults, setAnimeResults] = useState<Anime[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const [initialLoad, setInitialLoad] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const [genres, setGenres] = useState<Genre[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<number[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | ''>('');
  const [hasSearched, setHasSearched] = useState(false);
  const [order, setOrder] = useState<string>('popularity_desc');
  const [totalResults, setTotalResults] = useState(0);  // 追加: 総検索結果数
  const [gameStatuses, setGameStatuses] = useState<{ [key: number]: number | null }>({});
  const [animeStatuses, setAnimeStatuses] = useState<Record<number, number>>({});
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [themes, setThemes] = useState<GameTheme[]>([]);
  const [selectedThemes, setSelectedThemes] = useState<number[]>([]);
  const [fromBottomNav, setFromBottomNav] = useState(false);

  // アニメ検索モード
  const [searchMode, setSearchMode] = useState<SearchMode>('game');
  const [selectedAnimeType, setSelectedAnimeType] = useState<string>('');
  const [selectedAnimeSeason, setSelectedAnimeSeason] = useState<string>('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q') || '';
    const pageParam = params.get('page');
    const genresParam = params.get('genres');
    const platformsParam = params.get('platforms');
    const yearParam = params.get('year');
    const orderParam = params.get('order');
    const themesParam = params.get('themes');
    const rawMode = (params.get('mode') || 'game') as SearchMode;
    const modeParam = (rawMode === 'anime' && !isDev()) ? 'game' : rawMode;
    const animeTypeParam = params.get('anime_type') || '';
    const animeSeasonParam = params.get('season') || '';

    // 初期表示時（URLパラメータが空）の場合、人気順をデフォルトで設定
    if (!location.search) {
      navigate('/search?order=popularity_desc', { replace: true });
      return;
    }

    setSearchMode(modeParam);
    setSearchQuery(query);
    const currentPage = pageParam ? parseInt(pageParam, 10) : 1;
    setPage(currentPage);
    const genres = genresParam ? genresParam.split(',').map(Number) : [];
    const platforms = platformsParam ? platformsParam.split(',').map(Number) : [];
    const year = yearParam ? parseInt(yearParam, 10) : '';
    const defaultOrder = modeParam === 'anime' ? 'score_desc' : 'popularity_desc';
    const order = orderParam || defaultOrder;
    const themes = themesParam ? themesParam.split(',').map(Number) : [];
    setSelectedGenres(genres);
    setSelectedPlatforms(platforms);
    setSelectedYear(year);
    setOrder(order);
    setSelectedThemes(themes);
    setSelectedAnimeType(animeTypeParam);
    setSelectedAnimeSeason(animeSeasonParam);

    // 常に検索を実行（空検索でも人気順で表示）
    setLoading(true);
    setInitialLoad(false);
    setHasSearched(true);

    if (modeParam === 'anime') {
      const animeFilters: AnimeSearchFilters = {
        type: animeTypeParam || undefined,
        season: animeSeasonParam || undefined,
        year: year || undefined,
        order: order === '' ? undefined : order,
      };

      searchAnime(query, currentPage, animeFilters)
        .then((data) => {
          setAnimeResults(data.results);
          setSearchResults([]);
          setTotalPages(Math.ceil(data.count / GAMES_PER_PAGE));
          setTotalResults(data.count);
        })
        .catch((error) => {
          console.error('検索中にエラーが発生しました:', error);
          setAnimeResults([]);
          setTotalPages(0);
          setTotalResults(0);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      const filters: SearchFilters = {
        genres,
        platforms,
        themes,
        year,
        order: order === '' ? undefined : order
      };

      searchGames(query, currentPage, filters)
        .then((data) => {
          setSearchResults(data.results);
          setAnimeResults([]);
          setTotalPages(Math.ceil(data.count / GAMES_PER_PAGE));
          setTotalResults(data.count);
        })
        .catch((error) => {
          console.error('検索中にエラーが発生しました:', error);
          setSearchResults([]);
          setTotalPages(0);
          setTotalResults(0);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [genresData, platformsData, themesData] = await Promise.all([
          getGenres(),
          getPlatforms(),
          getThemes()
        ]);
        setGenres(genresData);
        setPlatforms(platformsData);
        setThemes(themesData);
      } catch (error) {
        console.error('フィルターの取得に失敗しました:', error);
        setGenres([]);
        setPlatforms([]);
        setThemes([]);
      }
    };
    fetchFilters();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    if (isLoggedIn && searchResults.length > 0) {
      const gameIds = searchResults.map(game => game.game_id);
      getBasicGameStatuses(gameIds).then(statuses => {
        setGameStatuses(statuses);
      });
    }
  }, [searchResults, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && animeResults.length > 0) {
      const malIds = animeResults.map(a => a.mal_id);
      getBasicAnimeStatuses(malIds).then(statuses => {
        setAnimeStatuses(statuses);
      });
    }
  }, [animeResults, isLoggedIn]);

  useEffect(() => {
    // ボトムナビゲーションからの遷移かどうかを判定
    const fromNav = location.state?.fromBottomNav === true;
    setFromBottomNav(fromNav);

    // PWAかつボトムナビゲーションからの遷移時のみフォーカス
    if (isPwa() && fromNav && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [location.state]);

  const handleGenreChange = (event: SelectChangeEvent<number[]>) => {
    setSelectedGenres(event.target.value as number[]);
  };

  const handlePlatformChange = (event: SelectChangeEvent<number[]>) => {
    setSelectedPlatforms(event.target.value as number[]);
  };

  const handleYearChange = (event: SelectChangeEvent<number | ''>) => {
    setSelectedYear(event.target.value as number | '');
  };

  const createSearchParams = (params: {
    q: string,
    page: string,
    genres: string,
    platforms: string,
    themes: string,
    year: string,
    order: string,
    mode?: string,
    anime_type?: string,
    season?: string,
  }) => {
    const searchParams = new URLSearchParams();

    if (params.mode && params.mode !== 'game') searchParams.set('mode', params.mode);
    if (params.q) searchParams.set('q', params.q);
    if (params.page !== '1') searchParams.set('page', params.page);
    if (params.genres) searchParams.set('genres', params.genres);
    if (params.platforms) searchParams.set('platforms', params.platforms);
    if (params.themes) searchParams.set('themes', params.themes);
    if (params.year) searchParams.set('year', params.year);
    if (params.order) searchParams.set('order', params.order);
    if (params.anime_type) searchParams.set('anime_type', params.anime_type);
    if (params.season) searchParams.set('season', params.season);

    return searchParams;
  };

  const buildCurrentParams = (overrides: Partial<{
    q: string, page: string, order: string, mode: string,
    anime_type: string, season: string, year: string,
    genres: string, platforms: string, themes: string,
  }> = {}) => {
    return createSearchParams({
      q: overrides.q ?? searchQuery,
      page: overrides.page ?? '1',
      genres: overrides.genres ?? (searchMode === 'game' ? selectedGenres.join(',') : ''),
      platforms: overrides.platforms ?? (searchMode === 'game' ? selectedPlatforms.join(',') : ''),
      themes: overrides.themes ?? (searchMode === 'game' ? selectedThemes.join(',') : ''),
      year: overrides.year ?? selectedYear.toString(),
      order: overrides.order ?? order,
      mode: overrides.mode ?? searchMode,
      anime_type: overrides.anime_type ?? (searchMode === 'anime' ? selectedAnimeType : ''),
      season: overrides.season ?? (searchMode === 'anime' ? selectedAnimeSeason : ''),
    });
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const params = buildCurrentParams();
    navigate(`/search?${params.toString()}`);
  };

  const handleOrderChange = (newOrder: string) => {
    setOrder(newOrder);
    const params = buildCurrentParams({ order: newOrder });
    navigate(`/search?${params.toString()}`);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    const params = buildCurrentParams({ page: value.toString() });
    navigate(`/search?${params.toString()}`);
    window.scrollTo(0, 0);
  };

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: SearchMode | null) => {
    if (newMode === null || newMode === searchMode) return;
    const defaultOrder = newMode === 'anime' ? 'popularity_desc' : 'popularity_desc';
    const params = createSearchParams({
      q: '',
      page: '1',
      genres: '',
      platforms: '',
      themes: '',
      year: '',
      order: defaultOrder,
      mode: newMode,
      anime_type: '',
      season: '',
    });
    navigate(`/search?${params.toString()}`);
  };

  return (
    <>
      <Grid container spacing={2}>
        {/* 左カラム: 検索ボックス */}
        <Grid item xs={12} md={3}>
          <Box sx={{
            position: { md: 'sticky' },
            top: { md: 80 },
            mb: { xs: 2, md: 0 }
          }}>
            {isDev() && (
              <ToggleButtonGroup
                value={searchMode}
                exclusive
                onChange={handleModeChange}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
              >
                <ToggleButton value="game" sx={{ fontSize: '0.75rem', textTransform: 'none', gap: 0.5 }}>
                  <SportsEsportsIcon sx={{ fontSize: '1rem' }} />
                  ゲーム
                </ToggleButton>
                <ToggleButton value="anime" sx={{ fontSize: '0.75rem', textTransform: 'none', gap: 0.5 }}>
                  <LiveTvIcon sx={{ fontSize: '1rem' }} />
                  アニメ
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            <Typography
              variant="h6"
              component="h1"
              sx={{
                mb: 2,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 500
              }}
            >
              検索フィルター
            </Typography>
            <form onSubmit={handleSearchSubmit}>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField
                    inputRef={searchInputRef}
                    size="small"
                    fullWidth
                    variant="outlined"
                    label={searchMode === 'anime' ? 'アニメ名を入力' : 'ゲーム名を入力'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                      '& .MuiInputLabel-root': {
                        fontSize: '0.8rem'
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.8rem'
                      }
                    }}
                  />
                </Grid>
                {searchMode === 'game' && (
                  <>
                    <Grid item xs={12}>
                      <Autocomplete
                        size="small"
                        multiple
                        options={[...genres, ...themes]}
                        getOptionLabel={(option: Genre | GameTheme) => option.name}
                        groupBy={(option: Genre | GameTheme) => 'genre_id' in option ? 'ジャンル' : 'テーマ'}
                        value={[
                          ...genres.filter((genre) => selectedGenres.includes(genre.genre_id)),
                          ...themes.filter((theme) => selectedThemes.includes(theme.theme_id))
                        ]}
                        onChange={(event, newValue) => {
                          const newGenres = newValue
                            .filter((item): item is Genre => 'genre_id' in item)
                            .map(item => item.genre_id);
                          const newThemes = newValue
                            .filter((item): item is GameTheme => 'theme_id' in item)
                            .map(item => item.theme_id);
                          setSelectedGenres(newGenres);
                          setSelectedThemes(newThemes);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label="ジャンル・テーマ"
                            placeholder="選択してください"
                            sx={{
                              '& .MuiInputLabel-root': {
                                fontSize: '0.8rem'
                              },
                              '& .MuiInputBase-input': {
                                fontSize: '0.8rem'
                              }
                            }}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option: Genre | GameTheme, index) => (
                            <Chip
                              size="small"
                              label={option.name}
                              {...getTagProps({ index })}
                              sx={{ fontSize: '0.7rem', height: '22px' }}
                            />
                          ))
                        }
                        sx={{
                          '& .MuiAutocomplete-tag': {
                            fontSize: '0.7rem'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        size="small"
                        multiple
                        options={platforms}
                        getOptionLabel={(option) => option.name}
                        value={platforms.filter((platform) => selectedPlatforms.includes(platform.platform_id))}
                        onChange={(event, newValue) => {
                          setSelectedPlatforms(newValue.map((platform) => platform.platform_id));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label="プラットフォーム"
                            placeholder="プラットフォームを選択"
                            sx={{
                              '& .MuiInputLabel-root': {
                                fontSize: '0.8rem'
                              },
                              '& .MuiInputBase-input': {
                                fontSize: '0.8rem'
                              }
                            }}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              size="small"
                              label={option.name}
                              {...getTagProps({ index })}
                              sx={{ fontSize: '0.7rem', height: '22px' }}
                            />
                          ))
                        }
                      />
                    </Grid>
                  </>
                )}
                {searchMode === 'anime' && (
                  <>
                    <Grid item xs={12}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel sx={{ fontSize: '0.8rem' }}>タイプ</InputLabel>
                        <Select
                          value={selectedAnimeType}
                          onChange={(e) => setSelectedAnimeType(e.target.value as string)}
                          label="タイプ"
                          sx={{
                            fontSize: '0.8rem',
                            '& .MuiSelect-select': { fontSize: '0.8rem' }
                          }}
                        >
                          <MenuItem value="" sx={{ fontSize: '0.8rem' }}>
                            <em>指定なし</em>
                          </MenuItem>
                          {ANIME_TYPES.map((type) => (
                            <MenuItem key={type} value={type} sx={{ fontSize: '0.8rem' }}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel sx={{ fontSize: '0.8rem' }}>シーズン</InputLabel>
                        <Select
                          value={selectedAnimeSeason}
                          onChange={(e) => setSelectedAnimeSeason(e.target.value as string)}
                          label="シーズン"
                          sx={{
                            fontSize: '0.8rem',
                            '& .MuiSelect-select': { fontSize: '0.8rem' }
                          }}
                        >
                          <MenuItem value="" sx={{ fontSize: '0.8rem' }}>
                            <em>指定なし</em>
                          </MenuItem>
                          {ANIME_SEASONS.map((s) => (
                            <MenuItem key={s.value} value={s.value} sx={{ fontSize: '0.8rem' }}>
                              {s.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel sx={{ fontSize: '0.8rem' }}>
                      {searchMode === 'anime' ? '放送年' : 'リリース年'}
                    </InputLabel>
                    <Select
                      value={selectedYear}
                      onChange={handleYearChange}
                      label={searchMode === 'anime' ? '放送年' : 'リリース年'}
                      sx={{
                        fontSize: '0.8rem',
                        '& .MuiSelect-select': {
                          fontSize: '0.8rem'
                        }
                      }}
                    >
                      <MenuItem value="" sx={{ fontSize: '0.8rem' }}>
                        <em>指定なし</em>
                      </MenuItem>
                      {YEAR_RANGE.map((year) => (
                        <MenuItem key={year} value={year} sx={{ fontSize: '0.8rem' }}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    検索
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Grid>

        {/* 右カラム: 検索結果 */}
        <Grid item xs={12} md={9}>
          {hasSearched && (
            <Box sx={{
              mb: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}
                >
                  {totalResults}件の検索結果
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                border: 'none',
                borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                padding: '2px 8px',
              }}>
                <SortIcon sx={{ mr: 0.5, color: 'action.active', fontSize: '0.9rem' }} />
                <Select
                  value={order}
                  onChange={(e) => handleOrderChange(e.target.value as string)}
                  displayEmpty
                  variant="standard"
                  sx={{
                    '&:before': { borderBottom: 'none' },
                    '&:after': { borderBottom: 'none' },
                    '& .MuiSelect-select': {
                      paddingBottom: 0,
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      paddingTop: '2px'
                    }
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.75rem' }}>並び替え</MenuItem>
                  {searchMode === 'game' ? [
                    <MenuItem key="release_date_desc" value="release_date_desc" sx={{ fontSize: '0.75rem' }}>発売日（新しい順）</MenuItem>,
                    <MenuItem key="release_date_asc" value="release_date_asc" sx={{ fontSize: '0.75rem' }}>発売日（古い順）</MenuItem>,
                    <MenuItem key="popularity_desc" value="popularity_desc" sx={{ fontSize: '0.75rem' }}>人気度 - JP/PULSE</MenuItem>,
                    <MenuItem key="popularity_en_desc" value="popularity_en_desc" sx={{ fontSize: '0.75rem' }}>人気度 - Global</MenuItem>,
                  ] : [
                    <MenuItem key="score_desc" value="score_desc" sx={{ fontSize: '0.75rem' }}>スコア順</MenuItem>,
                    <MenuItem key="popularity_desc" value="popularity_desc" sx={{ fontSize: '0.75rem' }}>人気順</MenuItem>,
                    <MenuItem key="members_desc" value="members_desc" sx={{ fontSize: '0.75rem' }}>メンバー数順</MenuItem>,
                    <MenuItem key="year_desc" value="year_desc" sx={{ fontSize: '0.75rem' }}>放送年（新しい順）</MenuItem>,
                    <MenuItem key="year_asc" value="year_asc" sx={{ fontSize: '0.75rem' }}>放送年（古い順）</MenuItem>,
                  ]}
                </Select>
              </Box>
            </Box>
          )}

          <Box>
            {initialLoad ? (
              <LoadingPlaceholder type="card" count={GAMES_PER_PAGE} columns={{ xs: 4, sm: 3, md: 2.4 }} />
            ) : loading ? (
              <LoadingPlaceholder type="card" count={GAMES_PER_PAGE} columns={{ xs: 4, sm: 3, md: 2.4 }} />
            ) : (searchMode === 'game' ? searchResults.length : animeResults.length) > 0 ? (
              <>
                <Grid container spacing={{ xs: 1, sm: 1 }}>
                  {searchMode === 'game' ? (
                    searchResults.map((game, index) => (
                      <React.Fragment key={game.game_id}>
                        <Grid item xs={4} sm={3} md={2.4}>
                          <GameCard
                            game={game}
                            initialStatus={gameStatuses[game.game_id]}
                          />
                        </Grid>
                        {isMobile && index === 11 && (
                          <Grid item xs={12}>
                            <AdvertisementResponsive />
                          </Grid>
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    animeResults.map((anime, index) => (
                      <React.Fragment key={anime.mal_id}>
                        <Grid item xs={4} sm={3} md={2.4}>
                          <AnimeCard anime={anime} initialStatus={animeStatuses[anime.mal_id]} />
                        </Grid>
                        {isMobile && index === 11 && (
                          <Grid item xs={12}>
                            <AdvertisementResponsive />
                          </Grid>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </Grid>
                {isMobile ? <RectangleAdvertisement /> : <Advertisement />}
                {totalPages > 1 && (
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    sx={{
                      mt: 3,
                      display: 'flex',
                      justifyContent: 'center',
                      '& .MuiPaginationItem-root': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }
                    }}
                  />
                )}
              </>
            ) : hasSearched ? (
              <Box sx={{
                textAlign: 'center',
                py: 6,
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                borderRadius: 2,
                mt: 2
              }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    color: 'text.secondary'
                  }}
                >
                  スペースを入れたり、英語やローマ字での検索もお試しください。
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default SearchPage;